// v1.0 - ITEM-43: Pure helpers for DocumentProcessingBanner - 2026-07-14
//
// No React or api.ts imports — safe to test without ESM/axios transform issues.
// All status-mapping and filtering logic lives here.

/** Minimal shape needed by helpers — mirrors DocumentListItem from api.ts. */
export interface BannerItem {
  session_id: string;
  filename: string;
  status: string;
  created_at: string;
  error_message?: string;
}

const IN_FLIGHT_STATUSES = new Set(['queued', 'processing']);
const TERMINAL_SUCCESS_STATUSES = new Set(['bronze_ready']);
const TERMINAL_ERROR_STATUSES = new Set(['failed', 'error']);

/** Human-readable label for a document status value. §4.16: no jargon. */
export function statusLabel(status: string): string {
  switch (status) {
    case 'queued':       return 'Queued — waiting to start';
    case 'processing':   return 'Preparing your document...';
    case 'bronze_ready': return 'Ready';
    case 'failed':       return 'Could not be processed — please try uploading again';
    case 'error':        return 'An error occurred — please try uploading again';
    default:             return 'Status unknown';
  }
}

/** True when the document is not yet in a terminal state. */
export function isInFlight(status: string): boolean {
  return IN_FLIGHT_STATUSES.has(status);
}

/** True when the document finished processing successfully. */
export function isTerminalSuccess(status: string): boolean {
  return TERMINAL_SUCCESS_STATUSES.has(status);
}

/** True when the document finished in an error state. */
export function isTerminalError(status: string): boolean {
  return TERMINAL_ERROR_STATUSES.has(status);
}

/** Filter to items created within the last 24 hours. */
export function filterBannerItems<T extends BannerItem>(docs: T[]): T[] {
  const cutoff = Date.now() - 24 * 60 * 60 * 1000;
  return docs.filter((d) => new Date(d.created_at).getTime() >= cutoff);
}
